/**
 * Minimal, typed, allocation-light event emitter.
 * `E` must be a discriminated union with a string `type` field.
 */
export type Listener<T> = (event: T) => void;
export type Unsubscribe = () => void;

export class TypedEmitter<E extends { readonly type: string }> {
  private readonly listeners = new Map<string, Set<Listener<E>>>();
  private readonly anyListeners = new Set<Listener<E>>();

  on<T extends E['type']>(type: T, listener: Listener<Extract<E, { type: T }>>): Unsubscribe {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    // The set is keyed by `type`, so every listener stored under it accepts that narrowed event.
    const wide = listener as Listener<E>;
    set.add(wide);
    return () => {
      set?.delete(wide);
    };
  }

  onAny(listener: Listener<E>): Unsubscribe {
    this.anyListeners.add(listener);
    return () => {
      this.anyListeners.delete(listener);
    };
  }

  emit(event: E): void {
    const set = this.listeners.get(event.type);
    if (set) for (const l of [...set]) l(event);
    if (this.anyListeners.size) for (const l of [...this.anyListeners]) l(event);
  }

  listenerCount(type?: E['type']): number {
    if (type === undefined) {
      let n = this.anyListeners.size;
      for (const s of this.listeners.values()) n += s.size;
      return n;
    }
    return this.listeners.get(type)?.size ?? 0;
  }

  clear(): void {
    this.listeners.clear();
    this.anyListeners.clear();
  }
}
